import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';

import Select, { components } from 'react-select';
import Flag from 'react-flags';
import countryList from 'country-list';
import languageList from 'language-list';

import { formatLanguageCodeEmoji } from '../../../../utils/formatHelpers';
import styles from './FormSelectField.module.css';

function FormSelectField({ hasBorder, error, label, type, value, options, onFormikChange, onChange, children, themeLight, isGrouped, groupBy, ...props }) {
    const { t } = useTranslation();
    const countries = useMemo(() => countryList.getData(), []);
    const languages = useMemo(() => languageList().getData(), []);
    let selectOptions = [];
    if (type === 'country' || type === 'nationality') {

        if (options?.length) {
            selectOptions = countries
                .filter(country => options.includes(country.code))
                .map(country => ({
                    label: `${t('general.' + type + '.' + country.code)}`,
                    value: country.code,
                    flag: country.code
                }));
        }
        else {
            selectOptions = countries.map(country => ({
                label: `${t('general.' + type + '.' + country.code)}`,
                value: country.code,
                flag: country.code
            }));
        }

    }

    if (type === 'language') {
        selectOptions = languages
            .filter(language => {
                if (language.code) {
                    return formatLanguageCodeEmoji(language.code.toUpperCase());
                }
                else {
                    return false;
                }
            })
            .map(language => ({
                label: `${t('general.' + type + '.' + language.code.toUpperCase())}`,
                value: language.code,
                flag: formatLanguageCodeEmoji(language.code.toUpperCase())
            }));

        selectOptions.push({
            label: `${t('general.' + type + '.UK')}`,
            value: 'uk',
            flag: formatLanguageCodeEmoji('UK')
        });

        selectOptions = selectOptions.sort((a, b) => a.label.localeCompare(b.label));
    }

    if (type === 'default') {
        if (isGrouped && groupBy && options.length > 0 && options[0][groupBy]) {
            // Group options by the specified property
            const groupedOptions = options.reduce((acc, option) => {
                const groupLabel = option[groupBy] || 'Other';
                
                if (!acc[groupLabel]) {
                    acc[groupLabel] = [];
                }
                
                acc[groupLabel].push({
                    label: option.label,
                    value: option.value,
                    roleTitle: option.roleTitle,
                    roleId: option.roleId
                });
                
                return acc;
            }, {});
            
            // Convert to the format required by react-select
            selectOptions = Object.keys(groupedOptions).map(groupLabel => ({
                label: groupLabel,
                options: groupedOptions[groupLabel]
            }));
        } else {
            selectOptions = options.map(option => ({
                label: option.label,
                value: option.value,
                ...(option.roleTitle && { roleTitle: option.roleTitle }),
                ...(option.roleId && { roleId: option.roleId })
            }));
        }
    }

    let wrapper_class = styles.wrapper;

    if (error) {
        wrapper_class += ' ' + styles.wrapper_error;
    }

    if (hasBorder) {
        wrapper_class += ' ' + styles.hasBorder;
    }
    if (themeLight) {
        wrapper_class += ' ' + styles.themeLight;
    }
    const Input = ({ ...rest }) => <components.Input {...rest} autoComplete={'do-not-autofill'} />;

    const customSingleValue = ({ data }) => {

        if (type === 'country' || type === 'nationality' || type === 'language') {
            return (
                <div className="country-select">
                    <Flag
                        name={data.flag}
                        format="svg"
                        shiny={false}
                        basePath="/vendor/flags"
                    />
                    {` ${data.label}`}
                </div>
            );
        }

        if (type === 'default') {
            return (
                <div className="default-select">{data.label}</div>
            );
        }

    }

    // Handler to adapt react-select's onChange event to Formik's expectations
    const handleChange = (selectedOption) => {

        if (onFormikChange) {

            if (Array.isArray(selectedOption)) {
                onFormikChange({
                    target: {
                        name: props.name,
                        value: selectedOption.map(option => option.value),
                    }
                });
            }
            else {
                onFormikChange({
                    target: {
                        name: props.name,
                        value: selectedOption ? selectedOption.value : '',
                    }
                });
            }

        }
        else if (onChange) {

            if (Array.isArray(selectedOption)) {
                onChange(selectedOption.map(option => option.value));
            }
            else {
                onChange(selectedOption ? selectedOption.value : '');
            }

        }

    }

    const filterByLabel = (option, inputValue) => {
        return option.data.label.toLowerCase().includes(inputValue.toLowerCase());
    }

    // Find matching options for the current value
    const findSelectedOptions = () => {
        // If options are grouped, we need to flatten them first
        let flatOptions = selectOptions;
        
        if (isGrouped && selectOptions.length > 0 && selectOptions[0].options) {
            flatOptions = selectOptions.reduce((acc, group) => {
                return [...acc, ...group.options];
            }, []);
        }
        
        return flatOptions.filter(option => {
            if (Array.isArray(value)) {
                return value.includes(option.value);
            } else {
                return option.value === value;
            }
        });
    };

    return (
        <div className={themeLight ? styles.fieldTheme : styles.field}>
            {label && <label htmlFor={props.name}>{label}</label>}
            <div className={wrapper_class}>
                <Select
                    classNamePrefix="react-select"
                    options={selectOptions}
                    value={findSelectedOptions()}
                    filterOption={filterByLabel}
                    getOptionLabel={option => (
                        <div>
                            {option.flag &&
                                <Flag
                                    name={option.flag}
                                    format="svg"
                                    shiny={false}
                                    basePath="/vendor/flags"
                                />
                            }
                            {` ${option.label}`}
                        </div>
                    )}
                    components={{
                        SingleValue: customSingleValue,
                        Input: Input
                    }}
                    onChange={handleChange}
                    {...props}
                />
            </div>
            {error && <p className={styles.error}>{error}</p>}
        </div>
    );
}

export default FormSelectField;